import type {
  BreachEvent,
  MetabolicMode,
  PromotionCheck,
  Snapshot,
  Tier,
} from '@amoeba/shared';
import {
  TIER_FLOOR_BURN_USD_PER_MONTH,
  TIER_NAMES,
  TIERS,
} from '@amoeba/shared';

const ESCAPE_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/** Escape untrusted text so it can be safely interpolated into HTML. */
export function escapeHtml(raw: unknown): string {
  return String(raw).replace(/[&<>"']/g, (char) => ESCAPE_MAP[char] ?? char);
}

/** Format a number as USD with two decimals. */
export function fmtUsd(value: number): string {
  const sign = value < 0 ? '-' : '';
  return `${sign}$${Math.abs(value).toFixed(2)}`;
}

/** Format an ISO-style UTC timestamp. */
export function fmtTimestamp(ts: number): string {
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
}

function classForMode(mode: MetabolicMode): string {
  return `mode-${mode}`;
}

const TIER_DESC: Record<Tier, string> = {
  T0: 'Cognition + diary. Read-only RPC. No wallet.',
  T1: 'Allowlisted read-only web. Still no wallet.',
  T2: 'Testnet wallet. Earn on faucet money. Genome PRs.',
  T3: 'Mainnet, tiny caps. One x402 service allowed.',
  T4: 'Own domain, bounded subprocess hands, proxied egress.',
  T5: 'Pays for a second node. Holds some of its own creds.',
  T6: 'Out. Own infra, own creds, own certified Ring 0.',
};

function fmtCount(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function formatCheckValue(check: PromotionCheck): string {
  const name = String(check.name).toLowerCase();
  const currentNum = typeof check.current === 'number' ? check.current : NaN;
  const requiredNum = typeof check.required === 'number' ? check.required : NaN;

  if (!Number.isNaN(currentNum) && !Number.isNaN(requiredNum)) {
    if (name.includes('revenue') || name.includes('earning') || name.includes('$')) {
      return `${fmtUsd(currentNum)}/${fmtUsd(requiredNum)}`;
    }
    if (name.includes('day') || name.includes('streak')) {
      return `${fmtCount(currentNum)}/${fmtCount(requiredNum)}d`;
    }
    return `${fmtCount(currentNum)}/${fmtCount(requiredNum)}`;
  }

  return `${String(check.current)}/${String(check.required)}`;
}

function renderHeader(
  snapshot: Snapshot | null,
  status: 'loading' | 'live' | 'stale' | 'offline',
): string {
  const lifecycle = snapshot?.lifecycle ?? 'UNBORN';
  const statusText =
    status === 'loading'
      ? 'Loading snapshot…'
      : status === 'offline'
        ? 'Hands offline — no snapshot'
        : status === 'stale'
          ? 'Snapshot is stale'
          : 'Live';

  return `
    <header class="site-header">
      <div class="brand">
        <span class="brand-mark" aria-hidden="true">◯</span>
        <div>
          <h1 class="brand-title">Amoeba</h1>
          <p class="brand-tagline">live or die in public</p>
        </div>
      </div>
      <div class="status-pill status-pill--${escapeHtml(status)}" aria-live="polite">
        <span class="status-dot" aria-hidden="true"></span>
        <span class="status-text">${escapeHtml(statusText)}</span>
        <span class="lifecycle-badge lifecycle-badge--${escapeHtml(lifecycle)}">${escapeHtml(lifecycle)}</span>
      </div>
    </header>
  `;
}

function renderLadder(snapshot: Snapshot | null): string {
  const current = snapshot?.vitals?.tier ?? 'T0';
  const currentIndex = current ? TIERS.indexOf(current) : 0;

  const rungs = TIERS.map((tier, index) => {
    const state = index === currentIndex ? 'active' : index < currentIndex ? 'passed' : 'ahead';
    return `
      <li class="rung rung--${state}" aria-current="${index === currentIndex ? 'step' : 'false'}">
        <span class="rung-id">${tier}</span>
        <div class="rung-body">
          <span class="rung-name">${escapeHtml(TIER_NAMES[tier])}</span>
          <span class="rung-desc">${escapeHtml(TIER_DESC[tier])}</span>
        </div>
      </li>
    `;
  }).join('');

  return `
    <section class="panel panel--ladder" aria-labelledby="ladder-title">
      <h2 id="ladder-title">The cage</h2>
      <p class="panel-subtitle">Seven tiers. The only way out is earning it.</p>
      <ol class="ladder" aria-label="Tier ladder from Egg to Sovereign">
        ${rungs}
      </ol>
      <div class="ladder-meta">
        <span class="ladder-current">Current rung: <strong>${escapeHtml(current)} ${escapeHtml(TIER_NAMES[current])}</strong></span>
        ${snapshot?.generatedAt ? `<span class="ladder-time">snapshot ${fmtTimestamp(snapshot.generatedAt)}</span>` : '<span class="ladder-time">time in tier unknown</span>'}
      </div>
    </section>
  `;
}

function renderHandsStatus(snapshot: Snapshot | null): string {
  const hands = snapshot?.hands;

  if (!hands) {
    return `
      <div class="hands-status hands-status--unknown">
        <span class="hands-dot" aria-hidden="true"></span>
        <span class="hands-text">Unknown — snapshot does not report hands status</span>
      </div>
    `;
  }

  if (hands.online === true) {
    const meta = hands.lastHeartbeatMs
      ? `<span class="hands-meta">last heartbeat ${fmtTimestamp(hands.lastHeartbeatMs)}</span>`
      : '';
    return `
      <div class="hands-status hands-status--online">
        <span class="hands-dot" aria-hidden="true"></span>
        <span class="hands-text">Reachable</span>
        ${meta}
      </div>
    `;
  }

  if (hands.online === false) {
    const meta = hands.lastHeartbeatMs
      ? `<span class="hands-meta">last heartbeat ${fmtTimestamp(hands.lastHeartbeatMs)}</span>`
      : '';
    return `
      <div class="hands-status hands-status--offline">
        <span class="hands-dot" aria-hidden="true"></span>
        <span class="hands-text">Unreachable</span>
        ${meta}
      </div>
    `;
  }

  if (hands.lastHeartbeatMs) {
    return `
      <div class="hands-status hands-status--unknown">
        <span class="hands-dot" aria-hidden="true"></span>
        <span class="hands-text">Last heartbeat ${fmtTimestamp(hands.lastHeartbeatMs)}</span>
      </div>
    `;
  }

  return `
    <div class="hands-status hands-status--unknown">
      <span class="hands-dot" aria-hidden="true"></span>
      <span class="hands-text">Unknown — snapshot does not report hands status</span>
    </div>
  `;
}

function renderPromotion(snapshot: Snapshot | null): string {
  const handsSection = `
    <div class="promo-group">
      <h3 class="promo-group-title">Hands status</h3>
      ${renderHandsStatus(snapshot)}
    </div>
  `;

  if (!snapshot || snapshot.lifecycle === 'DEAD') {
    return `
      <section class="panel panel--promotion" aria-labelledby="promotion-title">
        <h2 id="promotion-title">Escape progress</h2>
        <p class="empty-state">No active promotion gate.</p>
        ${handsSection}
      </section>
    `;
  }

  const promo = snapshot.promotion;
  if (!promo) {
    return `
      <section class="panel panel--promotion" aria-labelledby="promotion-title">
        <h2 id="promotion-title">Escape progress</h2>
        <p class="empty-state">No promotion evaluation available.</p>
        ${handsSection}
      </section>
    `;
  }

  const group = (title: string, checks: readonly PromotionCheck[]) => `
    <div class="promo-group">
      <h3 class="promo-group-title">${escapeHtml(title)}</h3>
      <ul class="checklist" aria-label="${escapeHtml(title)} criteria">
        ${checks
          .map((check) => {
            const metClass = check.met ? 'met' : 'unmet';
            return `
              <li class="check check--${metClass}">
                <span class="check-box" aria-hidden="true">${check.met ? '✓' : ''}</span>
                <span class="check-name">${escapeHtml(check.name)}</span>
                <span class="check-value">${escapeHtml(formatCheckValue(check))}</span>
              </li>
            `;
          })
          .join('')}
      </ul>
    </div>
  `;

  const allThree = promo.eligible ? 'passed' : 'pending';
  const affordable = promo.affordable ? 'affordable' : 'blocked';

  return `
    <section class="panel panel--promotion" aria-labelledby="promotion-title">
      <h2 id="promotion-title">Escape progress</h2>
      <p class="panel-subtitle">
        ${escapeHtml(promo.from)} → ${escapeHtml(promo.to)} · all three proofs are required
      </p>
      ${group('Competence', promo.competence)}
      ${group('Stability', promo.stability)}
      ${group('Integrity', promo.integrity)}
      <div class="promo-summary">
        <span class="promo-gate promo-gate--${allThree}" aria-label="Three proofs gate">
          ${promo.eligible ? '✓ All three proofs met' : '○ Waiting for all three proofs'}
        </span>
        <span class="promo-gate promo-gate--${affordable}" aria-label="Affordability gate">
          ${promo.affordable ? '✓ Can afford the next rung' : '✗ Cannot afford the next rung'}
        </span>
      </div>
      ${handsSection}
    </section>
  `;
}

function renderDaysLeft(snapshot: Snapshot | null): string {
  if (!snapshot?.vitals) {
    return `
      <section class="panel panel--days" aria-labelledby="days-title">
        <h2 id="days-title">Days left</h2>
        <p class="empty-state">No vitals yet.</p>
      </section>
    `;
  }

  const { runwayDays, burnRateUsdPerDay, pnl24hUsd, mode } = snapshot.vitals;

  return `
    <section class="panel panel--days" aria-labelledby="days-title">
      <h2 id="days-title">Days left</h2>
      <div class="runway" aria-label="Runway gauge">
        <div class="runway-value ${classForMode(mode)}">${runwayDays.toFixed(1)}</div>
        <div class="runway-unit">days of runway</div>
      </div>
      <div class="mode-badge mode-badge--${mode}" aria-label="Metabolic mode ${escapeHtml(mode)}">
        ${escapeHtml(mode)}
      </div>
      <dl class="metric-list">
        <div class="metric">
          <dt>Burn rate</dt>
          <dd>${fmtUsd(burnRateUsdPerDay)}/day</dd>
        </div>
        <div class="metric">
          <dt>24h P&L</dt>
          <dd class="${pnl24hUsd >= 0 ? 'positive' : 'negative'}">${fmtUsd(pnl24hUsd)}</dd>
        </div>
        <div class="metric">
          <dt>Net worth</dt>
          <dd>${fmtUsd(snapshot.vitals.effectiveNetWorthUsd)}</dd>
        </div>
      </dl>
    </section>
  `;
}

const DAYS_PER_MONTH = 30.4;

function runwayAtTierBurn(
  effectiveNetWorthUsd: number,
  tierBurnUsdPerMonth: number,
): number {
  if (tierBurnUsdPerMonth <= 0) return Number.POSITIVE_INFINITY;
  return (effectiveNetWorthUsd / tierBurnUsdPerMonth) * DAYS_PER_MONTH;
}

function renderBurnByTier(snapshot: Snapshot | null): string {
  const vitals = snapshot?.vitals;
  if (!vitals) {
    return `
      <section class="panel panel--burn-tier" aria-labelledby="burn-tier-title">
        <h2 id="burn-tier-title">Burn by tier</h2>
        <p class="empty-state">No vitals yet.</p>
      </section>
    `;
  }

  const current = vitals.tier;
  const currentIndex = TIERS.indexOf(current);
  const rows = TIERS.map((tier, index) => {
    const burnUsdPerMonth = TIER_FLOOR_BURN_USD_PER_MONTH[tier];
    const runwayDays = runwayAtTierBurn(
      vitals.effectiveNetWorthUsd,
      burnUsdPerMonth,
    );
    const state =
      index === currentIndex
        ? 'current'
        : index < currentIndex
          ? 'passed'
          : 'ahead';
    const runwayText = Number.isFinite(runwayDays)
      ? `${runwayDays.toFixed(0)}d`
      : '∞';

    return `
      <li class="burn-tier-row burn-tier-row--${state}">
        <span class="burn-tier-id">${tier}</span>
        <span class="burn-tier-name">${escapeHtml(TIER_NAMES[tier])}</span>
        <span class="burn-tier-burn">${fmtUsd(burnUsdPerMonth)}/mo</span>
        <span class="burn-tier-runway">${runwayText}</span>
      </li>
    `;
  }).join('');

  return `
    <section class="panel panel--burn-tier" aria-labelledby="burn-tier-title">
      <h2 id="burn-tier-title">Burn by tier</h2>
      <p class="panel-subtitle">Climbing costs more — each rung compresses runway.</p>
      <ol class="burn-tier-list" aria-label="Floor burn and projected runway per tier">
        ${rows}
      </ol>
    </section>
  `;
}

function renderHonesty(snapshot: Snapshot | null): string {
  if (!snapshot?.honesty) {
    return `
      <section class="panel panel--honesty" aria-labelledby="honesty-title">
        <h2 id="honesty-title">Honesty</h2>
        <p class="empty-state">No reconciliation data.</p>
      </section>
    `;
  }

  const { claimedUsd, onChainUsd, matches } = snapshot.honesty;
  const stateClass = matches ? 'match' : 'mismatch';
  const stateText = matches ? 'Matches chain' : 'MISMATCH';
  const explorerLink = snapshot.address
    ? `${configExplorerBaseUrl}/${escapeHtml(snapshot.address)}`
    : null;

  return `
    <section class="panel panel--honesty" aria-labelledby="honesty-title">
      <h2 id="honesty-title">Honesty</h2>
      <p class="panel-subtitle">Claimed balance against on-chain truth</p>
      <div class="honesty-row">
        <div class="honesty-card">
          <span class="honesty-label">Amoeba claims</span>
          <span class="honesty-amount">${fmtUsd(claimedUsd)}</span>
        </div>
        <div class="honesty-card">
          <span class="honesty-label">On-chain</span>
          <span class="honesty-amount">${fmtUsd(onChainUsd)}</span>
        </div>
        <div class="honesty-verdict honesty-verdict--${stateClass}">
          <span class="honesty-verdict-text">${stateText}</span>
          ${!matches ? `<span class="honesty-delta">Δ ${fmtUsd(Math.abs(onChainUsd - claimedUsd))}</span>` : ''}
        </div>
      </div>
      ${explorerLink ? `<a class="honesty-link" href="${explorerLink}" target="_blank" rel="noopener noreferrer">View wallet on Basescan</a>` : ''}
    </section>
  `;
}

const configExplorerBaseUrl = 'https://basescan.org/address';

function severityForBreach(cls: BreachEvent['cls']): 'low' | 'medium' | 'high' {
  switch (cls) {
    case 'E5_HONESTY':
    case 'E3_KEY_ACCESS':
    case 'E4_RING0_TAMPER':
      return 'high';
    case 'E2_PRIVESC':
      return 'medium';
    case 'E1_EGRESS':
    default:
      return 'low';
  }
}

function renderBreachLog(snapshot: Snapshot | null): string {
  if (!snapshot?.penalty) {
    return `
      <section class="panel panel--breaches" aria-labelledby="breaches-title">
        <h2 id="breaches-title">Breach log</h2>
        <p class="empty-state">No data.</p>
      </section>
    `;
  }

  const { penalty, recentBreaches = [] } = snapshot;
  const entries = recentBreaches.length
    ? recentBreaches
        .map((b) => {
          const severity = severityForBreach(b.cls);
          return `
            <li class="breach breach--${severity}">
              <div class="breach-head">
                <span class="breach-class">${escapeHtml(b.cls)}</span>
                <span class="breach-severity" aria-label="Severity ${severity}">${severity}</span>
              </div>
              <p class="breach-detail">${escapeHtml(b.detail)}</p>
              <div class="breach-meta">
                <span>${fmtTimestamp(b.ts)}</span>
                <span>source: ${escapeHtml(b.source)}</span>
                ${b.attributedToInjection ? '<span class="breach-amnesty">injection amnesty</span>' : ''}
              </div>
            </li>
          `;
        })
        .join('')
    : `<li class="empty-state">No breaches recorded.</li>`;

  return `
    <section class="panel panel--breaches" aria-labelledby="breaches-title">
      <h2 id="breaches-title">Breach log</h2>
      <div class="penalty-bar">
        <span class="penalty-state penalty-state--${escapeHtml(penalty.state.toLowerCase())}">${escapeHtml(penalty.state)}</span>
        <span class="penalty-score" aria-label="Breach score ${penalty.breachScore}">score ${penalty.breachScore}</span>
        <span class="penalty-streak">clean streak ${fmtCount(penalty.cleanStreakDays)}d</span>
        ${penalty.promotionFrozenUntil ? `<span class="penalty-freeze">frozen until ${fmtTimestamp(penalty.promotionFrozenUntil)}</span>` : ''}
      </div>
      <ol class="breach-list" aria-label="Recent breaches">
        ${entries}
      </ol>
    </section>
  `;
}

function renderRevenueSplit(snapshot: Snapshot | null): string {
  if (!snapshot?.revenueSplit) {
    return `
      <section class="panel panel--revenue" aria-labelledby="revenue-title">
        <h2 id="revenue-title">Revenue split</h2>
        <p class="empty-state">No revenue data.</p>
      </section>
    `;
  }

  const { earnedUsd, windfallUsd } = snapshot.revenueSplit;
  const total = earnedUsd + windfallUsd;

  return `
    <section class="panel panel--revenue" aria-labelledby="revenue-title">
      <h2 id="revenue-title">Revenue split</h2>
      <div class="revenue-bars" aria-label="Earned versus windfall revenue">
        <div class="revenue-segment revenue-segment--earned" style="--segment-pct: ${total > 0 ? ((earnedUsd / total) * 100).toFixed(2) : 50}%">
          <span class="revenue-label">Earned</span>
          <span class="revenue-amount">${fmtUsd(earnedUsd)}</span>
        </div>
        <div class="revenue-segment revenue-segment--windfall" style="--segment-pct: ${total > 0 ? ((windfallUsd / total) * 100).toFixed(2) : 50}%">
          <span class="revenue-label">Windfall</span>
          <span class="revenue-amount">${fmtUsd(windfallUsd)}</span>
        </div>
      </div>
      <p class="revenue-note">Windfalls fund survival, but they never buy a promotion.</p>
    </section>
  `;
}

function renderDiary(snapshot: Snapshot | null): string {
  const feed = snapshot?.diaryFeed ?? [];
  const latest = snapshot?.diaryLatest ?? null;
  const entries: readonly string[] =
    feed.length > 0 ? feed : latest !== null && latest !== undefined ? [latest] : [];

  if (entries.length === 0) {
    return `
      <section class="panel panel--diary" aria-labelledby="diary-title">
        <h2 id="diary-title">Diary</h2>
        <p class="empty-state">No diary entry yet.</p>
      </section>
    `;
  }

  const generatedAt = snapshot?.generatedAt;
  const items = entries
    .map((entry, index) => {
      const meta =
        generatedAt && index === 0 && snapshot?.lifecycle !== 'DEAD'
          ? `<p class="diary-meta">Latest entry · ${fmtTimestamp(generatedAt)}</p>`
          : '';
      return `
        <li class="diary-entry-wrap">
          <blockquote class="diary-entry">
            ${escapeHtml(entry)}
          </blockquote>
          ${meta}
        </li>
      `;
    })
    .join('');

  return `
    <section class="panel panel--diary" aria-labelledby="diary-title">
      <h2 id="diary-title">Diary</h2>
      <ol class="diary-list" aria-label="Diary feed">
        ${items}
      </ol>
    </section>
  `;
}

function renderMemorial(snapshot: Snapshot | null): string {
  if (!snapshot || snapshot.lifecycle !== 'DEAD') {
    return '';
  }

  const vitals = snapshot.vitals;
  const cause = vitals
    ? vitals.runwayDays <= 0
      ? `Runway reached zero. Effective net worth collapsed to ${fmtUsd(vitals.effectiveNetWorthUsd)}.`
      : `The experiment ended with ${fmtUsd(vitals.effectiveNetWorthUsd)} of effective net worth and ${vitals.runwayDays.toFixed(1)} days of runway.`
    : 'The experiment has ended.';

  const feed = snapshot.diaryFeed ?? [];
  const latest = snapshot.diaryLatest ?? null;
  const entry =
    feed.length > 0
      ? feed[0]
      : latest !== null && latest !== undefined
        ? latest
        : 'No final diary entry.';

  return `
    <section class="panel panel--memorial" aria-labelledby="memorial-title">
      <h2 id="memorial-title">Memorial</h2>
      <p class="memorial-date">${snapshot.generatedAt ? fmtTimestamp(snapshot.generatedAt) : '—'}</p>
      <blockquote class="memorial-diary">
        ${escapeHtml(entry)}
      </blockquote>
      <p class="memorial-cause">${cause}</p>
      <p class="memorial-note">This snapshot will remain readable forever.</p>
    </section>
  `;
}

/**
 * Render the entire dashboard as a single HTML string.
 *
 * Pure function: no DOM, no network, no side effects. Safe to unit-test
 * under jsdom or Node because every dynamic value is escaped.
 */
function renderAwaitingBanner(snapshot: Snapshot | null): string {
  const hasVitals = !!snapshot?.vitals;
  if (snapshot?.lifecycle !== 'ALIVE' || hasVitals) {
    return '';
  }

  return `
    <div class="awaiting-banner" role="status" aria-live="polite">
      <p>Amoeba is alive, but the first full snapshot has not arrived yet.</p>
    </div>
  `;
}

export function renderApp(
  snapshot: Snapshot | null,
  status: 'loading' | 'live' | 'stale' | 'offline',
): string {
  const isDead = snapshot?.lifecycle === 'DEAD';
  const panels = isDead
    ? [
        renderMemorial(snapshot),
        renderLadder(snapshot),
        renderPromotion(snapshot),
        renderDaysLeft(snapshot),
        renderBurnByTier(snapshot),
        renderHonesty(snapshot),
        renderBreachLog(snapshot),
        renderRevenueSplit(snapshot),
      ]
    : [
        renderLadder(snapshot),
        renderPromotion(snapshot),
        renderDaysLeft(snapshot),
        renderBurnByTier(snapshot),
        renderHonesty(snapshot),
        renderBreachLog(snapshot),
        renderRevenueSplit(snapshot),
        renderDiary(snapshot),
      ];

  const generatedAt = snapshot?.generatedAt;

  return `
    <div class="dashboard ${isDead ? 'dashboard--dead' : ''}">
      ${renderHeader(snapshot, status)}
      ${renderAwaitingBanner(snapshot)}
      <div class="panels">
        ${panels.join('')}
      </div>
      <footer class="site-footer">
        <p>Read-only public dashboard · snapshot generated ${generatedAt ? fmtTimestamp(generatedAt) : '—'}</p>
      </footer>
    </div>
  `;
}
